import { zodResolver } from "@hookform/resolvers/zod";
import { Badge } from "@veil/ui/components/badge";
import { CloudflareIcon, LetsEncryptIcon } from "@veil/ui/components/brand";
import {
	Field,
	FieldContent,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLabel,
	FieldLegend,
	FieldSet,
} from "@veil/ui/components/field";
import { Input } from "@veil/ui/components/input";
import { RadioGroup } from "@veil/ui/components/radio-group";
import { Switch } from "@veil/ui/components/switch";
import { Textarea } from "@veil/ui/components/textarea";
import FileKey from "lucide-react/dist/esm/icons/file-key";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { ComponentType, ReactNode } from "react";
import { useCallback } from "react";
import { Controller, type UseFormReturn, useForm } from "react-hook-form";
import { z } from "zod";
import { ChoiceCard, InlineChoice } from "@/components/form/choice-card";
import { EASE_OUT_EXPO } from "@/lib/motion";
import type { CfFullCertSource, CfMode, TlsMode, TlsRequest } from "./tls-api";
import { TlsFlowDiagram } from "./tls-flow-diagram";

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PEM_CERT_RE = /^-----BEGIN CERTIFICATE-----/;
const PEM_KEY_RE = /^-----BEGIN [A-Z ]*PRIVATE KEY-----/;

// ---------------------------------------------------------------------------
// Schema — the zod source of truth for the flat TLS form model
// ---------------------------------------------------------------------------

export const TlsFormSchema = z
	.object({
		mode: z.enum(["cloudflare", "byo", "acme"]),
		cfMode: z.enum(["flexible", "full"]),
		fullCertSource: z.enum(["self-signed", "origin-ca"]),
		originLock: z.boolean(),
		mtlsEnabled: z.boolean(),
		zoneCaPem: z.string(),
		originCaToken: z.string(),
		email: z.string(),
		cert: z.string(),
		key: z.string(),
	})
	.superRefine((v, ctx) => {
		// Branch rules: only the selected mode's fields are validated.
		if (v.mode === "acme") {
			const email = v.email.trim();
			if (email && !EMAIL_RE.test(email)) {
				ctx.addIssue({
					code: "custom",
					path: ["email"],
					message: "Enter a valid email address.",
				});
			}
		}
		if (v.mode === "byo") {
			const cert = v.cert.trim();
			const key = v.key.trim();
			if (!cert) {
				ctx.addIssue({
					code: "custom",
					path: ["cert"],
					message: "Paste the fullchain certificate.",
				});
			} else if (!PEM_CERT_RE.test(cert)) {
				ctx.addIssue({
					code: "custom",
					path: ["cert"],
					message: "Must begin with -----BEGIN CERTIFICATE-----.",
				});
			}
			if (!key) {
				ctx.addIssue({
					code: "custom",
					path: ["key"],
					message: "Paste the private key.",
				});
			} else if (!PEM_KEY_RE.test(key)) {
				ctx.addIssue({
					code: "custom",
					path: ["key"],
					message: "Must begin with -----BEGIN PRIVATE KEY-----.",
				});
			}
		}
		if (v.mode === "cloudflare" && v.cfMode === "full") {
			if (v.fullCertSource === "origin-ca" && !v.originCaToken.trim()) {
				ctx.addIssue({
					code: "custom",
					path: ["originCaToken"],
					message:
						"Paste a Cloudflare API token with SSL and Certificates edit access.",
				});
			}
			const zoneCa = v.zoneCaPem.trim();
			if (v.mtlsEnabled && zoneCa && !PEM_CERT_RE.test(zoneCa)) {
				ctx.addIssue({
					code: "custom",
					path: ["zoneCaPem"],
					message: "Must begin with -----BEGIN CERTIFICATE-----.",
				});
			}
		}
	});

/** Flat form value covering every branch of the discriminated TLS model. */
export type TlsFormValue = z.infer<typeof TlsFormSchema>;

export type TlsForm = UseFormReturn<TlsFormValue>;

export interface UseTlsFormResult {
	form: TlsForm;
	/** Live snapshot of the form (subscribes to all fields). */
	value: TlsFormValue;
	/** True for flows that kick a streamed issuance Operation. */
	willIssue: boolean;
	/** Build the discriminated request body from the current values. */
	buildRequest: () => TlsRequest;
}

export function tlsFormDefaults(initial?: Partial<TlsFormValue>): TlsFormValue {
	return {
		mode: initial?.mode ?? "cloudflare",
		cfMode: initial?.cfMode ?? "flexible",
		fullCertSource: initial?.fullCertSource ?? "self-signed",
		originLock: initial?.originLock ?? true,
		mtlsEnabled: initial?.mtlsEnabled ?? false,
		zoneCaPem: initial?.zoneCaPem ?? "",
		originCaToken: initial?.originCaToken ?? "",
		email: initial?.email ?? "",
		cert: initial?.cert ?? "",
		key: initial?.key ?? "",
	};
}

/**
 * react-hook-form + zod owner of the TLS form for the new discriminated
 * `POST /api/system/tls` model. Shared by the setup wizard step and the
 * cluster settings "Security & access" group so both speak the same contract.
 * Validation runs on submit (then re-validates on change), with per-field
 * messages surfaced inline next to the offending input.
 */
export function useTlsForm(initial?: Partial<TlsFormValue>): UseTlsFormResult {
	const form = useForm<TlsFormValue>({
		resolver: zodResolver(TlsFormSchema),
		defaultValues: tlsFormDefaults(initial),
		mode: "onTouched",
	});

	const value = form.watch();
	const isFull = value.mode === "cloudflare" && value.cfMode === "full";
	const willIssue =
		value.mode === "acme" || (isFull && value.fullCertSource === "origin-ca");

	const buildRequest = useCallback((): TlsRequest => {
		const v = form.getValues();
		if (v.mode === "acme") {
			return { mode: "acme", email: v.email.trim() || undefined };
		}
		if (v.mode === "byo") {
			return { mode: "byo", cert: v.cert, key: v.key };
		}
		// cloudflare
		if (v.cfMode === "flexible") {
			return {
				mode: "cloudflare",
				cfMode: "flexible",
				originLock: v.originLock,
			};
		}
		// cloudflare + full
		const trimmedZone = v.zoneCaPem.trim();
		const mtls = {
			enabled: v.mtlsEnabled,
			...(v.mtlsEnabled && trimmedZone ? { zoneCaPem: trimmedZone } : {}),
		};
		if (v.fullCertSource === "origin-ca") {
			return {
				mode: "cloudflare",
				cfMode: "full",
				fullCertSource: "origin-ca",
				originCaToken: v.originCaToken.trim(),
				originLock: v.originLock,
				mtls,
			};
		}
		return {
			mode: "cloudflare",
			cfMode: "full",
			fullCertSource: "self-signed",
			originLock: v.originLock,
			mtls,
		};
	}, [form]);

	return { form, value, willIssue, buildRequest };
}

// ---------------------------------------------------------------------------
// Human labels
// ---------------------------------------------------------------------------

export function tlsModeLabel(
	value: Pick<TlsFormValue, "mode" | "cfMode" | "fullCertSource">,
): string {
	switch (value.mode) {
		case "acme":
			return "Automatic HTTPS";
		case "byo":
			return "Custom certificate";
		default:
			return value.cfMode === "full"
				? `Cloudflare · Full (${
						value.fullCertSource === "origin-ca" ? "Origin CA" : "self-signed"
					})`
				: "Cloudflare · Flexible";
	}
}

// ---------------------------------------------------------------------------
// Selector — flat two-level layout
// ---------------------------------------------------------------------------

interface ModeDef {
	value: TlsMode;
	title: string;
	description: string;
	icon: ComponentType<{ className?: string }>;
	recommended?: boolean;
	/** Requires a public hostname (edge presents a real vhost / origin cert). */
	needsHostname?: boolean;
	panelTitle: string;
}

const MODES: ModeDef[] = [
	{
		value: "acme",
		title: "Automatic HTTPS",
		description:
			"Free Let's Encrypt certificate, issued and auto-renewed on this host.",
		icon: LetsEncryptIcon,
		recommended: true,
		needsHostname: true,
		panelTitle: "Automatic HTTPS",
	},
	{
		value: "cloudflare",
		title: "Cloudflare",
		description: "Front the origin with Cloudflare (Flexible or Full).",
		icon: CloudflareIcon,
		panelTitle: "Cloudflare configuration",
	},
	{
		value: "byo",
		title: "Bring your own certificate",
		description: "Paste a PEM certificate + private key stored on this host.",
		icon: FileKey,
		needsHostname: true,
		panelTitle: "Certificate details",
	},
];

/**
 * The three-option TLS selector. The mode radio group stays visually intact
 * (no inline expansion); the selected branch's configuration renders in a
 * single panel below the whole group, so options never push each other apart.
 * Shared by the wizard step and settings group; each caller owns the
 * surrounding chrome (save action, issuance timeline, cert download).
 */
export function TlsModeSelect({
	form,
	hostname,
	hostnameAvailable,
	idPrefix = "tls",
	disabled = false,
}: {
	form: TlsForm;
	hostname: string | null;
	hostnameAvailable: boolean;
	idPrefix?: string;
	disabled?: boolean;
}) {
	const reduce = !!useReducedMotion();
	const mode = form.watch("mode");
	const cfMode = form.watch("cfMode");
	const fullCertSource = form.watch("fullCertSource");
	const originLock = form.watch("originLock");
	const mtlsEnabled = form.watch("mtlsEnabled");
	const selected: ModeDef =
		MODES.find((m) => m.value === mode) ?? (MODES[0] as ModeDef);

	return (
		<div className="flex flex-col gap-4">
			<Controller
				control={form.control}
				name="mode"
				render={({ field }) => (
					<RadioGroup
						value={field.value}
						onValueChange={(v) => field.onChange(v as TlsMode)}
						aria-label="TLS mode"
						className="gap-2"
						disabled={disabled}
					>
						{MODES.map((m) => {
							const optDisabled =
								disabled || (!!m.needsHostname && !hostnameAvailable);
							return (
								<ChoiceCard
									key={m.value}
									id={`${idPrefix}-${m.value}`}
									value={m.value}
									disabled={optDisabled}
									icon={m.icon}
									title={
										<>
											{m.title}
											{m.recommended && (
												<Badge variant="secondary" className="font-normal">
													Recommended
												</Badge>
											)}
										</>
									}
									description={
										m.needsHostname && !hostnameAvailable
											? `${m.description} Needs a public hostname.`
											: m.description
									}
								/>
							);
						})}
					</RadioGroup>
				)}
			/>

			<AnimatePresence mode="wait" initial={false}>
				<motion.section
					key={mode}
					initial={reduce ? false : { opacity: 0, y: -4 }}
					animate={{ opacity: 1, y: 0 }}
					exit={reduce ? undefined : { opacity: 0, y: -4 }}
					transition={{ duration: 0.18, ease: EASE_OUT_EXPO }}
					aria-label={selected.panelTitle}
					className="@container rounded-lg border border-border/70 bg-muted/20"
				>
					<header className="border-b border-border/60 px-4 py-2.5">
						<h3 className="text-[0.68rem] font-medium uppercase tracking-[0.14em] text-muted-foreground">
							{selected.panelTitle}
						</h3>
					</header>
					{/* Container-query split: the panel lives in a narrow settings
					    column on some surfaces, so the rail keys off the panel's own
					    width, not the viewport's. */}
					<div className="grid gap-5 px-4 py-4 @4xl:grid-cols-[minmax(0,1fr)_20rem] @4xl:gap-6">
						<FieldGroup className="order-2 @4xl:order-1">
							{mode === "cloudflare" && (
								<CloudflarePanel
									form={form}
									hostnameAvailable={hostnameAvailable}
									idPrefix={idPrefix}
									disabled={disabled}
									reduce={reduce}
								/>
							)}
							{mode === "byo" && (
								<ByoPanel form={form} idPrefix={idPrefix} disabled={disabled} />
							)}
							{mode === "acme" && (
								<AcmePanel
									form={form}
									hostname={hostname}
									idPrefix={idPrefix}
									disabled={disabled}
								/>
							)}
						</FieldGroup>
						<aside className="order-1 min-w-0 @4xl:order-2">
							<TlsFlowDiagram
								mode={mode}
								cfMode={cfMode}
								fullCertSource={fullCertSource}
								originLock={originLock}
								mtlsEnabled={mtlsEnabled}
							/>
						</aside>
					</div>
				</motion.section>
			</AnimatePresence>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Panels
// ---------------------------------------------------------------------------

function CloudflarePanel({
	form,
	hostnameAvailable,
	idPrefix,
	disabled,
	reduce,
}: {
	form: TlsForm;
	hostnameAvailable: boolean;
	idPrefix: string;
	disabled: boolean;
	reduce: boolean;
}) {
	const cfMode = form.watch("cfMode");
	const fullCertSource = form.watch("fullCertSource");
	const mtlsEnabled = form.watch("mtlsEnabled");
	const errors = form.formState.errors;
	const isFull = cfMode === "full";

	return (
		<>
			{/* Encryption mode: side-by-side segmented pair */}
			<FieldSet>
				<SectionLegend>Encryption mode</SectionLegend>
				<Controller
					control={form.control}
					name="cfMode"
					render={({ field }) => (
						<RadioGroup
							value={field.value}
							onValueChange={(v) => field.onChange(v as CfMode)}
							aria-label="Cloudflare encryption mode"
							className="grid-cols-1 sm:grid-cols-2"
							disabled={disabled}
						>
							<ChoiceCard
								id={`${idPrefix}-cf-flexible`}
								value="flexible"
								disabled={disabled}
								title="Flexible"
								description="Cloudflare terminates TLS; origin serves plain HTTP on :80."
							/>
							<ChoiceCard
								id={`${idPrefix}-cf-full`}
								value="full"
								disabled={disabled || !hostnameAvailable}
								title="Full"
								description={
									hostnameAvailable
										? "Origin serves HTTPS on :443; Cloudflare connects over TLS."
										: "Origin serves HTTPS on :443. Needs a public hostname."
								}
							/>
						</RadioGroup>
					)}
				/>
				{!isFull && (
					<FieldDescription>
						TLS ends at the Cloudflare edge — no origin certificate is stored on
						this host.
					</FieldDescription>
				)}
			</FieldSet>

			{/* Origin certificate: Full only */}
			<AnimatePresence initial={false}>
				{isFull && (
					<motion.div
						key="origin-cert"
						initial={reduce ? false : { opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: "auto" }}
						exit={reduce ? undefined : { opacity: 0, height: 0 }}
						transition={{ duration: 0.2, ease: EASE_OUT_EXPO }}
						className="overflow-hidden"
					>
						<FieldSet>
							<SectionLegend>Origin certificate</SectionLegend>
							<Controller
								control={form.control}
								name="fullCertSource"
								render={({ field }) => (
									<RadioGroup
										value={field.value}
										onValueChange={(v) => field.onChange(v as CfFullCertSource)}
										aria-label="Origin certificate source"
										className="gap-1"
										disabled={disabled}
									>
										<InlineChoice
											id={`${idPrefix}-cf-selfsigned`}
											value="self-signed"
											disabled={disabled}
											title={
												<>
													Self-signed
													<Badge variant="secondary" className="font-normal">
														Recommended
													</Badge>
												</>
											}
											description="Generated on this host. Valid for Cloudflare Full."
										/>
										<InlineChoice
											id={`${idPrefix}-cf-originca`}
											value="origin-ca"
											disabled={disabled}
											title="Cloudflare Origin CA"
											description="15-year cert issued via the Cloudflare API. Enables Full (strict)."
										/>
									</RadioGroup>
								)}
							/>
							<AnimatePresence initial={false}>
								{fullCertSource === "origin-ca" && (
									<motion.div
										key="origin-ca-token"
										initial={reduce ? false : { opacity: 0, height: 0 }}
										animate={{ opacity: 1, height: "auto" }}
										exit={reduce ? undefined : { opacity: 0, height: 0 }}
										transition={{ duration: 0.2, ease: EASE_OUT_EXPO }}
										className="overflow-hidden"
									>
										<Field
											data-invalid={!!errors.originCaToken}
											className="pt-1"
										>
											<FieldLabel htmlFor={`${idPrefix}-cf-token`}>
												Cloudflare API token
											</FieldLabel>
											<Input
												id={`${idPrefix}-cf-token`}
												type="password"
												autoComplete="off"
												placeholder="Cloudflare API token"
												aria-invalid={!!errors.originCaToken}
												disabled={disabled}
												{...form.register("originCaToken")}
											/>
											{errors.originCaToken ? (
												<FieldError>{errors.originCaToken.message}</FieldError>
											) : (
												<FieldDescription>
													Used once to issue the certificate, then discarded —
													the token is never stored on this host.
												</FieldDescription>
											)}
										</Field>
									</motion.div>
								)}
							</AnimatePresence>
						</FieldSet>
					</motion.div>
				)}
			</AnimatePresence>

			{/* Security: one deduped group */}
			<FieldSet>
				<SectionLegend>Security</SectionLegend>
				<Controller
					control={form.control}
					name="originLock"
					render={({ field }) => (
						<SwitchField
							id={`${idPrefix}-originlock`}
							title="Lock origin to Cloudflare IPs"
							description="Reject direct connections that don't originate from Cloudflare's IP ranges (loopback always allowed)."
							checked={field.value}
							onChange={field.onChange}
							disabled={disabled}
						/>
					)}
				/>
				{isFull && (
					<>
						<Controller
							control={form.control}
							name="mtlsEnabled"
							render={({ field }) => (
								<SwitchField
									id={`${idPrefix}-cf-mtls`}
									title="Authenticated Origin Pulls (mTLS)"
									description="Require a Cloudflare client certificate on connections to the origin."
									checked={field.value}
									onChange={field.onChange}
									disabled={disabled}
								/>
							)}
						/>
						<AnimatePresence initial={false}>
							{mtlsEnabled && (
								<motion.div
									key="zone-ca"
									initial={reduce ? false : { opacity: 0, height: 0 }}
									animate={{ opacity: 1, height: "auto" }}
									exit={reduce ? undefined : { opacity: 0, height: 0 }}
									transition={{ duration: 0.2, ease: EASE_OUT_EXPO }}
									className="overflow-hidden"
								>
									<Field data-invalid={!!errors.zoneCaPem}>
										<FieldLabel htmlFor={`${idPrefix}-cf-zoneca`}>
											Origin-pull CA (PEM){" "}
											<span className="text-muted-foreground">(optional)</span>
										</FieldLabel>
										<Textarea
											id={`${idPrefix}-cf-zoneca`}
											rows={4}
											className="font-mono text-xs"
											aria-invalid={!!errors.zoneCaPem}
											placeholder={"-----BEGIN CERTIFICATE-----\n…"}
											disabled={disabled}
											{...form.register("zoneCaPem")}
										/>
										{errors.zoneCaPem ? (
											<FieldError>{errors.zoneCaPem.message}</FieldError>
										) : (
											<FieldDescription>
												Leave blank to use Cloudflare's bundled global CA.
												Upload a zone-level CA to restrict pulls to your zone.
											</FieldDescription>
										)}
									</Field>
								</motion.div>
							)}
						</AnimatePresence>
					</>
				)}
			</FieldSet>
		</>
	);
}

function ByoPanel({
	form,
	idPrefix,
	disabled,
}: {
	form: TlsForm;
	idPrefix: string;
	disabled: boolean;
}) {
	const errors = form.formState.errors;
	return (
		<>
			<Field data-invalid={!!errors.cert}>
				<FieldLabel htmlFor={`${idPrefix}-cert`}>
					Certificate (fullchain PEM)
				</FieldLabel>
				<Textarea
					id={`${idPrefix}-cert`}
					rows={5}
					className="font-mono text-xs"
					aria-invalid={!!errors.cert}
					placeholder={"-----BEGIN CERTIFICATE-----\n…"}
					disabled={disabled}
					{...form.register("cert")}
				/>
				{errors.cert && <FieldError>{errors.cert.message}</FieldError>}
			</Field>
			<Field data-invalid={!!errors.key}>
				<FieldLabel htmlFor={`${idPrefix}-key`}>Private key (PEM)</FieldLabel>
				<Textarea
					id={`${idPrefix}-key`}
					rows={5}
					className="font-mono text-xs"
					aria-invalid={!!errors.key}
					placeholder={"-----BEGIN PRIVATE KEY-----\n…"}
					disabled={disabled}
					{...form.register("key")}
				/>
				{errors.key ? (
					<FieldError>{errors.key.message}</FieldError>
				) : (
					<FieldDescription>
						Stored on the cm-state volume under{" "}
						<code>/srv/cluster-manager/tls/</code>. Never leaves this box.
					</FieldDescription>
				)}
			</Field>
		</>
	);
}

function AcmePanel({
	form,
	hostname,
	idPrefix,
	disabled,
}: {
	form: TlsForm;
	hostname: string | null;
	idPrefix: string;
	disabled: boolean;
}) {
	const errors = form.formState.errors;
	const host = hostname ?? "your domain";
	return (
		<>
			<div>
				<p className="text-xs font-medium">Before issuing, confirm:</p>
				<ul className="mt-1.5 flex flex-col gap-1 text-xs text-muted-foreground">
					<li className="flex gap-2">
						<span className="text-muted-foreground/60">•</span>
						<span>
							An <code>A</code> record for <code>{host}</code> points at this
							server's public IP.
						</span>
					</li>
					<li className="flex gap-2">
						<span className="text-muted-foreground/60">•</span>
						<span>Ports 80 and 443 are open to the internet.</span>
					</li>
				</ul>
			</div>
			<Field data-invalid={!!errors.email}>
				<FieldLabel htmlFor={`${idPrefix}-email`}>
					Contact email{" "}
					<span className="text-muted-foreground">(optional)</span>
				</FieldLabel>
				<Input
					id={`${idPrefix}-email`}
					type="email"
					placeholder="ops@example.com"
					aria-invalid={!!errors.email}
					disabled={disabled}
					{...form.register("email")}
				/>
				{errors.email ? (
					<FieldError>{errors.email.message}</FieldError>
				) : (
					<FieldDescription>
						Let's Encrypt uses this for expiry notices. Leave blank to skip.
					</FieldDescription>
				)}
			</Field>
		</>
	);
}

// ---------------------------------------------------------------------------
// Primitives
//
// Thin compositions over the shared Field primitives. `FieldLabel` already
// carries the choice-card chrome and its checked treatment keys off the
// `data-checked` attribute the radio and switch items emit, so selection state
// never has to be threaded through as a prop.
// ---------------------------------------------------------------------------

/** Group legend styled as the panel's small-caps eyebrow. */
function SectionLegend({ children }: { children: ReactNode }) {
	return (
		<FieldLegend
			variant="label"
			className="text-[0.68rem] tracking-[0.14em] text-muted-foreground uppercase"
		>
			{children}
		</FieldLegend>
	);
}

/** Settings-style switch row: label + description on the left, switch right. */
function SwitchField({
	id,
	title,
	description,
	checked,
	onChange,
	disabled,
}: {
	id: string;
	title: string;
	description: string;
	checked: boolean;
	onChange: (v: boolean) => void;
	disabled?: boolean;
}) {
	return (
		<Field orientation="horizontal" data-disabled={disabled || undefined}>
			<FieldContent>
				<FieldLabel htmlFor={id}>{title}</FieldLabel>
				<FieldDescription>{description}</FieldDescription>
			</FieldContent>
			<Switch
				id={id}
				checked={checked}
				onCheckedChange={onChange}
				disabled={disabled}
			/>
		</Field>
	);
}
